/**
 * Shared horizontal scroll / min width rules for time-series charts that sync
 * scroll with Volume by Origin (StackedBarChart) and Price Pulse line charts.
 *
 * Vertical grid line spacing (X categories) stays at least categoryWidth px; when
 * the container is narrower than that minimum, horizontal scroll appears.
 */

/** Width of the fixed Y-axis column when charts use horizontal scroll (matches axis width + left margin). */
/** Slightly wider than 72px axis + margin so rotated price axis labels fit. */
export const CHART_SCROLL_Y_AXIS_GUTTER_PX = 108;

/** Left margin inside the gutter chart, so its axis sits at the column's right edge. */
export const CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN = 35;
/** Phone equivalent — the 35px desktop inset is pure padding on a 390px screen. */
export const CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE = 8;

/**
 * Width of the fixed Y-axis column, given the width its axis actually needs.
 *
 * Desktop keeps the hard-coded 108px. On phones the column tracks the measured
 * axis width (see `getChartYAxisWidth`) plus the smaller inset, which reclaims
 * ~40px of a 390px viewport. Safe to vary: the gutter/plot alignment code only
 * syncs the two charts *vertically* (it matches Y-axis spine length and top
 * offset via `gutterPlotMarginsY`), so horizontal width is independent.
 *
 * @param {object}  opts
 * @param {boolean} opts.isPhone
 * @param {number}  opts.yAxisWidth - from `getChartYAxisWidth`
 */
export function getChartScrollYAxisGutterPx({ isPhone, yAxisWidth } = {}) {
  if (!isPhone) return CHART_SCROLL_Y_AXIS_GUTTER_PX;
  // +2 mirrors the gutter chart's right margin, which insets its axis spine.
  return yAxisWidth + CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE + 2;
}

export const MIN_BAR_WIDTH = 47; // Minimum width per category (bar group or line chart day)
export const MIN_BAR_WIDTH_COMPACT = 30; // Compact mode for shorter ranges (≤ MAX_BARS_FOR_COMPACT points)
export const MAX_VISIBLE_BARS = 8; // Prefer scrolling once we exceed this many visible categories
export const MAX_BARS_FOR_COMPACT = 24; // Use compact category width for up to this many points

/** Per-day min width for FOB / Terminal Price Pulse when range > 24 days (matches Volume 47px × 2 per category). */
export const MIN_LINE_DAY_WIDTH_LONG_RANGE = MIN_BAR_WIDTH * 2;
/** Per-day min width for FOB / Terminal Price Pulse when range ≤ 24 days (matches Volume compact 30px × 2). */
export const MIN_LINE_DAY_WIDTH_COMPACT_RANGE = MIN_BAR_WIDTH_COMPACT * 2;

/**
 * @param {object} opts
 * @param {number} opts.dataPointCount
 * @param {number} opts.containerWidth - measured width of the chart area (scroll viewport)
 * @param {number} [opts.categoryMultiplier=1] - e.g. 2 when Volume chart shows current + last year side by side
 */
export function getHorizontalScrollChartDimensions({
  dataPointCount,
  containerWidth,
  categoryMultiplier = 1,
}) {
  const useCompactBars = dataPointCount <= MAX_BARS_FOR_COMPACT;
  const barWidth = useCompactBars ? MIN_BAR_WIDTH_COMPACT : MIN_BAR_WIDTH;
  const minWidthNeeded = dataPointCount * barWidth * categoryMultiplier;
  const needsScroll =
    minWidthNeeded > containerWidth || dataPointCount > MAX_VISIBLE_BARS;
  const chartWidth = needsScroll ? Math.max(minWidthNeeded, containerWidth) : containerWidth;

  return { needsScroll, chartWidth, useCompactBars, minWidthNeeded };
}

/**
 * FOB / Terminal Price Pulse: one X tick per day.
 * For ranges > 24 days use 94px/day (same horizontal density as Volume by Origin’s 47px × 2).
 * For ≤24 days use compact 30px/day (same as Volume short-range compact bars).
 */
export function getLineChartHorizontalScrollDimensions({ dataPointCount, containerWidth }) {
  const categoryWidth =
    dataPointCount > MAX_BARS_FOR_COMPACT
      ? MIN_LINE_DAY_WIDTH_LONG_RANGE
      : MIN_LINE_DAY_WIDTH_COMPACT_RANGE;
  const minWidthNeeded = dataPointCount * categoryWidth;
  const needsScroll =
    minWidthNeeded > containerWidth || dataPointCount > MAX_VISIBLE_BARS;
  const chartWidth = needsScroll ? Math.max(minWidthNeeded, containerWidth) : containerWidth;

  return { needsScroll, chartWidth, minWidthNeeded };
}
